import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Rss, Bot, Image as ImageIcon, Search, Send, Users, ShieldCheck, Cpu } from "lucide-react";

import { SeoHead } from "@/components/seo/SeoHead";
import { Section } from "@/components/marketing/Section";
import { Eyebrow } from "@/components/marketing/Eyebrow";
import { DisplayHeading } from "@/components/marketing/DisplayHeading";
import { Rule } from "@/components/marketing/Rule";
import { CTABanner } from "@/components/marketing/CTABanner";

import {
  buildOrganizationJsonLd,
  buildBreadcrumbJsonLd,
  buildItemListJsonLd,
} from "@/lib/seo";
import { cn } from "@/lib/utils";
import { useLocale, intlLocale, toLocale, dbLocaleSuffix } from "@/lib/locale";

interface Anchor {
  id: string;
  key: string;
  icon: typeof Rss;
}

const ANCHORS: Anchor[] = [
  { id: "sources", key: "sources", icon: Rss },
  { id: "generation", key: "generation", icon: Bot },
  { id: "images", key: "images", icon: ImageIcon },
  { id: "seo", key: "seo", icon: Search },
  { id: "publication", key: "publication", icon: Send },
  { id: "equipe", key: "equipe", icon: Users },
  { id: "conformite", key: "conformite", icon: ShieldCheck },
  { id: "modeles-ia", key: "modelesIa", icon: Cpu },
];

const MODELS_TABLE = [
  { key: "haiku", task: "rewrite", credits: 2, tier: "starter", speed: "fast" },
  { key: "sonnet", task: "rewrite", credits: 4, tier: "growth", speed: "medium" },
  { key: "opus", task: "rewrite", credits: 6, tier: "growth", speed: "slow" },
  { key: "gpt5", task: "rewrite", credits: 5, tier: "network", speed: "medium" },
  { key: "gemini", task: "rewrite", credits: 2, tier: "starter", speed: "fast" },
  { key: "flux-schnell", task: "image", credits: 1, tier: "starter", speed: "fast" },
  { key: "flux-pro", task: "image", credits: 3, tier: "growth", speed: "medium" },
  { key: "flux-ultra", task: "image", credits: 4, tier: "growth", speed: "medium" },
  { key: "imagen4", task: "image", credits: 3, tier: "growth", speed: "medium" },
  { key: "dalle3", task: "image", credits: 3, tier: "network", speed: "medium" },
];

export default function FeaturesPage() {
  const { t, i18n } = useTranslation();
  const uiLocale = useLocale();
  const lang = dbLocaleSuffix(uiLocale);
  const [active, setActive] = useState<string>(ANCHORS[0].id);
  const refs = useRef<Map<string, HTMLElement | null>>(new Map());

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const id = (entry.target as HTMLElement).id;
            if (id) setActive(id);
          }
        }
      },
      { rootMargin: "-30% 0px -55% 0px", threshold: [0, 0.5, 1] },
    );
    refs.current.forEach((el) => {
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const jsonLd = [
    buildOrganizationJsonLd(),
    buildBreadcrumbJsonLd([
      { name: t("marketing.breadcrumbs.home"), path: "/" },
      { name: t("marketing.header.nav.features"), path: "/fonctionnalites" },
    ]),
    buildItemListJsonLd(
      ANCHORS,
      (a, i) => ({
        "@type": "WebPageElement",
        position: i + 1,
        name: t(`marketing.features.sections.${a.key}.title`),
        url: `/fonctionnalites#${a.id}`,
      }),
    ),
  ];

  return (
    <>
      <SeoHead
        title={t("marketing.features.seo.title")}
        description={t("marketing.features.seo.description")}
        path="/fonctionnalites"
        locale={uiLocale}
        jsonLd={jsonLd}
      />

      <Section padding="lg">
        <div className="max-w-3xl">
          <Eyebrow>{t("marketing.features.hero.eyebrow")}</Eyebrow>
          <DisplayHeading level={1} className="mt-4">{t("marketing.features.hero.title")}</DisplayHeading>
          <p className="mt-6 text-lg leading-relaxed text-muted-foreground md:text-xl">{t("marketing.features.hero.lead")}</p>
        </div>
      </Section>

      <Rule />

      <div className="mx-auto max-w-[1200px] px-5 py-12 md:px-6">
        <div className="grid gap-12 lg:grid-cols-[220px_1fr]">
          <aside className="hidden lg:block">
            <div className="sticky top-32">
              <p className="dateline mb-4">{t("marketing.features.toc")}</p>
              <nav>
                <ul className="space-y-2 border-l border-rule pl-4">
                  {ANCHORS.map((a) => {
                    const Icon = a.icon;
                    const isActive = active === a.id;
                    return (
                      <li key={a.id}>
                        <a
                          href={`#${a.id}`}
                          className={cn(
                            "flex items-center gap-2 py-1 text-sm transition-colors",
                            isActive ? "text-accent-press font-medium" : "text-muted-foreground hover:text-foreground",
                          )}
                          aria-current={isActive ? "true" : undefined}
                        >
                          <Icon className="h-3.5 w-3.5" />
                          {t(`marketing.features.sections.${a.key}.title`)}
                        </a>
                      </li>
                    );
                  })}
                </ul>
              </nav>
            </div>
          </aside>

          <div className="space-y-20">
            {ANCHORS.map((a) => {
              const Icon = a.icon;
              const isModels = a.key === "modelesIa";
              return (
                <section
                  key={a.id}
                  id={a.id}
                  ref={(el) => {
                    refs.current.set(a.id, el);
                  }}
                  className="scroll-mt-28"
                >
                  <div className="flex items-center gap-3">
                    <Icon className="h-5 w-5 text-accent-press" />
                    <Eyebrow>{t(`marketing.features.sections.${a.key}.eyebrow`)}</Eyebrow>
                  </div>
                  <DisplayHeading level={2} className="mt-4">{t(`marketing.features.sections.${a.key}.title`)}</DisplayHeading>
                  <div className="mt-6 space-y-4 text-base leading-relaxed text-muted-foreground">
                    <p>{t(`marketing.features.sections.${a.key}.p1`)}</p>
                    <p>{t(`marketing.features.sections.${a.key}.p2`)}</p>
                  </div>

                  {a.key === "generation" && (
                    <div className="mt-8 border border-rule">
                      <table className="w-full text-left text-sm">
                        <thead>
                          <tr className="border-b border-rule bg-paper">
                            <th className="px-4 py-3 dateline">{t("marketing.features.modelsTable.model")}</th>
                            <th className="px-4 py-3 dateline">{t("marketing.features.modelsTable.useCase")}</th>
                            <th className="px-4 py-3 dateline text-right">{t("marketing.features.modelsTable.credits")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {["haiku", "sonnet", "opus", "gpt5", "gemini"].map((m) => (
                            <tr key={m} className="border-b border-rule last:border-b-0">
                              <td className="px-4 py-3 text-foreground">{t(`marketing.features.modelsCompact.${m}.name`)}</td>
                              <td className="px-4 py-3 text-muted-foreground">{t(`marketing.features.modelsCompact.${m}.useCase`)}</td>
                              <td className="px-4 py-3 text-right tabular-nums text-foreground">
                                {t(`marketing.features.modelsCompact.${m}.credits`)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {isModels && (
                    <div className="mt-8 overflow-x-auto border border-rule">
                      <table className="w-full min-w-[640px] text-left text-sm">
                        <thead>
                          <tr className="border-b border-rule bg-paper">
                            <th className="px-4 py-3 dateline">{t("marketing.features.modelsTable.model")}</th>
                            <th className="px-4 py-3 dateline">{t("marketing.features.modelsTable.task")}</th>
                            <th className="px-4 py-3 dateline text-right">{t("marketing.features.modelsTable.credits")}</th>
                            <th className="px-4 py-3 dateline">{t("marketing.features.modelsTable.tier")}</th>
                            <th className="px-4 py-3 dateline">{t("marketing.features.modelsTable.speed")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {MODELS_TABLE.map((row) => (
                            <tr key={row.key} className="border-b border-rule last:border-b-0">
                              <td className="px-4 py-3 text-foreground">{t(`marketing.features.modelsFull.${row.key}.name`)}</td>
                              <td className="px-4 py-3 text-muted-foreground">{t(`marketing.features.modelsFull.${row.key}.task`)}</td>
                              <td className="px-4 py-3 text-right tabular-nums">{row.credits}</td>
                              <td className="px-4 py-3 text-muted-foreground">{t(`marketing.features.tiers.${row.tier}`)}</td>
                              <td className="px-4 py-3 text-muted-foreground">{t(`marketing.features.speed.${row.speed}`)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </section>
              );
            })}

            <div className="pt-8">
              <Link to="/comment-ca-marche" className="inline-flex items-center gap-2 text-sm font-medium text-foreground hover:text-accent-press">
                {t("marketing.features.seeHowItWorks")} <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      <CTABanner
        tone="ink"
        headline={t("marketing.home.finalCta.title")}
        subhead={t("marketing.home.finalCta.subhead")}
        primary={{ label: t("marketing.home.finalCta.primary"), to: "/dpadmin?intent=signup" }}
        secondary={{ label: t("marketing.home.finalCta.secondary"), to: "/contact" }}
      />
    </>
  );
}
